import { useContext, FC, useRef, useState } from "react"
import { ExportFormatType, ExportGapFilterType, HSDFormatType, NetworkProviderComparisonType, NetworkProviderFilterType, ProviderDropdownTypes } from "../enumerations"
import { QESCheckbox, QESSelect } from "@app/shared/qes-standards";
import { SelectFormat, SelectSpecialtyFilter } from "./filters";
import { ReportSettingsProps } from "./report-settings/reportSettings";
import { ReportPanelBodyBaseProps } from "./reportPanelBodyBase";
import GenerateBody from "./generate-body";
import * as toast from "./toast";
import * as helpers from "./exportPanelBodyHelpers";
import { ExportAdequacyServicingProvidersModel } from "../exportModels";
import { NetworkAssessmentAnalysisType, NextClosestProviderAction, PatientVolumeLabelEnum, ProviderNetworkType } from "@app/projects/enumerators";
import ExportService from "../exportService";
import { FeatureFlagContext } from "../../feature-flags/FeatureFlagContext";
import { Features } from "../../feature-flags";

interface ZipCodeServicingProviderProps extends ReportPanelBodyBaseProps, ReportSettingsProps {
  providerIds: number[];
  hasMarketProviders: boolean;
  hasProspectProviders: boolean;
  activeNetworkType: ProviderNetworkType;
}

const initialSpecialtyFilter = {
  filterType: ExportGapFilterType.All,
  includeExpandableSpecialties: false,
  includeFacilitySpecialties: true,
  includePhysicianSpecialties: true,
};

const providerTypes = [
  { key: ProviderDropdownTypes.Network, value: { comparedReportName: "Changed Network Servicing Providers", reportName: "Distinct Network Servicing Providers" } },
  { key: ProviderDropdownTypes.Prospect, value: { comparedReportName: "Changed Prospect Servicing Providers", reportName: "Distinct Prospect Servicing Providers" } },
  { key: ProviderDropdownTypes.Market, value: { comparedReportName: "Changed Market Servicing Providers", reportName: "Distinct Market Servicing Providers" } },
];

const providerNetworkMap: Record<ProviderNetworkType, ProviderDropdownTypes> = {
  [ProviderNetworkType.Network]: ProviderDropdownTypes.Network,
  [ProviderNetworkType.Prospect]: ProviderDropdownTypes.Prospect,
  [ProviderNetworkType.Market]: ProviderDropdownTypes.Market,
  [ProviderNetworkType.Unknown]: ProviderDropdownTypes.NextClosestProviders,
  [ProviderNetworkType.NetNew]: ProviderDropdownTypes.NetNew,
};

const providerDropdownMap: Record<ProviderDropdownTypes, ProviderNetworkType> = {
  [ProviderDropdownTypes.Network]: ProviderNetworkType.Network,
  [ProviderDropdownTypes.Prospect]: ProviderNetworkType.Prospect,
  [ProviderDropdownTypes.Market]: ProviderNetworkType.Market,
  [ProviderDropdownTypes.NextClosestProviders]: ProviderNetworkType.Unknown,
  [ProviderDropdownTypes.NetNew]: ProviderNetworkType.NetNew,
};

export const ZipCodeServicingProvidersPanel: FC<ZipCodeServicingProviderProps> = (props: ZipCodeServicingProviderProps) => {
  const exportService = useRef(new ExportService()).current;
  const [selectedProviderType, setSelectedProviderType] = useState(() => {
    const selectedProvider = providerTypes.find(p => p.key === providerNetworkMap[props.activeNetworkType]) || providerTypes[0];
    return selectedProvider;
  });
  const [specialtyFilter, setSpecialtyFilter] = useState(initialSpecialtyFilter);
  const [outputFormat, setOutputFormat] = useState(ExportFormatType.CSV);
  const hsdFormat = useRef(HSDFormatType.DISTINCT_SERVICING_PROVIDER_REPORT).current;
  const [emailOnSuccess, setEmailOnSuccess] = useState(true);
  const getReportNameByComparedRunConfig = () => helpers.reportName(props.comparedRunConfigurationId ? selectedProviderType.value.comparedReportName : selectedProviderType.value.reportName);
  const [reportName, setReportName] = useState(() => getReportNameByComparedRunConfig());
  const [sharedUsers, setSharedUsers] = useState([]);
  const [includeAddedProviders, setIncludeAddedProviders] = useState(true);
  const [includeMatchedProviders, setIncludeMatchedProviders] = useState(true);
  const [includeRemovedProviders, setIncludeRemovedProviders] = useState(true);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);

  const handleSpecialtyIncludeChange = (selected: boolean, isPhysician: boolean) => {
    const newSpecialtyFilter = { ...specialtyFilter };

    if (isPhysician) {
      newSpecialtyFilter.includePhysicianSpecialties = selected;
    } else {
      newSpecialtyFilter.includeFacilitySpecialties = selected;
    }

    setSpecialtyFilter(newSpecialtyFilter);
  };

  const handleSpecialtyGapFilterChange = (filterType: ExportGapFilterType) => {
    const newSpecialtyFilter = { ...specialtyFilter };
    newSpecialtyFilter.filterType = filterType;
    setSpecialtyFilter(newSpecialtyFilter);
  };

  const handleOutputFormatChange = (exportFormat: ExportFormatType) => {
    setOutputFormat(exportFormat);
  };

  const handleGenerateClick = async () => {
    await generateExportAdequacyServicingProvidersModel();

    toast.toastInfo(reportName);
  }

  const generateExportAdequacyServicingProvidersModel = async () => {
    const providerComparisonTypes = [];
    if (!!props.comparedRunConfigurationId) {
      if (includeAddedProviders)
        providerComparisonTypes.push(NetworkProviderComparisonType.Added);
      if (includeMatchedProviders)
        providerComparisonTypes.push(NetworkProviderComparisonType.Matched);
      if (includeRemovedProviders)
        providerComparisonTypes.push(NetworkProviderComparisonType.Removed);
    } else {
      providerComparisonTypes.push(NetworkProviderComparisonType.Added, NetworkProviderComparisonType.Matched, NetworkProviderComparisonType.Removed);
    }

    const servicingProviderModel: ExportAdequacyServicingProvidersModel = {
      appendPatternOfCareProviders: false,
      comparedProjectRunConfigurationId: props.comparedRunConfigurationId,
      customProjectId: props.projectId,
      eMailOnSuccess: emailOnSuccess,
      exportName: reportName,
      formatType: outputFormat,
      hsdFormat: hsdFormat,
      impactProviderType: null,
      includeActiveRegionSets: true,
      includeApprovedRegionSets: false,
      includeFacilitySpecialties: specialtyFilter.includeFacilitySpecialties,
      includePendingRegionSets: false,
      includePhysicianSpecialties: specialtyFilter.includePhysicianSpecialties,
      isZipAnalysis: true,
      networkAssessmentAnalysisTypes: [NetworkAssessmentAnalysisType.ServiceArea],
      networkProviderFilterType: NetworkProviderFilterType.Available,
      nextClosestProviderAction: NextClosestProviderAction.None,
      projectRunConfigurationId: props.runConfigurationId,
      projectURL: props.projectURL,
      providerClaimsVolumeFilter: PatientVolumeLabelEnum.Unknown,
      providerComparisonTypes,
      providerIds: props.providerIds,
      providerNetworkType: providerDropdownMap[selectedProviderType.key],
      regionSetGapFilter: null,
      regionSetIds: props.regionSetIds,
      runAsynchronous: true,
      sharedUsers: sharedUsers,
      specialtyGapFilter: specialtyFilter.filterType,
      specialtyGroupCodes: props.specialtyGroupCodes,
    };

    await exportService.postExportAdequacyServicingProviders(servicingProviderModel);
  }

  const handleProviderTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = providerTypes.find(option => option.key === event.target.value);
    if (selected) {
      setSelectedProviderType(selected);
      setReportName(getReportNameByComparedRunConfig());
    }
  };

  const filteredProviderTypes = providerTypes.filter((type) => {
    if (!enabledFeatureFlags.has(Features.ZIP_PROSPECT_AND_MARKET) || (!props.hasMarketProviders && !props.hasProspectProviders) || !!props.comparedRunConfigurationId) {
      return type.key === ProviderDropdownTypes.Network;
    }
    if (!props.hasMarketProviders) {
      return type.key !== ProviderDropdownTypes.Market;
    }
    if (!props.hasProspectProviders) {
      return type.key !== ProviderDropdownTypes.Prospect;
    }
    return true;
  });
  return (
    <>
      <div className="row">
        <div className="col-3">
          <p className="lead mb0 font-weight-bold">Provider Filter</p>
          <p>Please select the providers to include:</p>
          <QESSelect
            value={selectedProviderType.key}
            onChange={handleProviderTypeChange}
            disabled={!!props.comparedRunConfigurationId}
            label="Provider Filter"
            hideLabel={true}
            testId="zip-code-servicing-providers-panel-body__provider-filter"
          >
            {filteredProviderTypes.map(opt => (
              <option key={opt.value.reportName} value={opt.key}>{opt.value.reportName}</option>
            ))}
          </QESSelect>

          <QESCheckbox
            label="Added Providers"
            checked={includeAddedProviders}
            onChange={(e) => setIncludeAddedProviders(e.target.checked)}
            testId="zip-code-servicing-providers-panel-body__added-providers"
          />
          <QESCheckbox
            label="Matched Providers"
            checked={includeMatchedProviders}
            onChange={(e) => setIncludeMatchedProviders(e.target.checked)}
            testId="zip-code-servicing-providers-panel-body__matched-providers"
          />
          <QESCheckbox
            label="Removed Providers"
            checked={includeRemovedProviders}
            onChange={(e) => setIncludeRemovedProviders(e.target.checked)}
            testId="zip-code-servicing-providers-panel-body__removed-providers"
          />
        </div>
        <div className="col-4">
          <SelectSpecialtyFilter
            adequacyAnalysisType="zip"
            disabled={false}
            onSpecialtyGapFilterChange={handleSpecialtyGapFilterChange}
            onSpecialtyIncludeChange={handleSpecialtyIncludeChange}
            onExpandableSpecialtyIncludeChange={() => void false}
            showExpandableSpecialtyOption={false}
            specialtyFilter={specialtyFilter}
            testIdSuffix={"zip-servicing-providers"}
          />
        </div>
        <div className="col-3">
          <SelectFormat
            outputFormat={outputFormat}
            onOutputFormatChange={handleOutputFormatChange}
            excelDisabled={false}
            exportType="specialty-summary-by-zip-code"
          />
        </div>
      </div>

      <GenerateBody
        {...props}
        emailOnSuccess={emailOnSuccess}
        reportName={reportName}
        onGenerateClick={handleGenerateClick}
        onEmailOnSuccessChange={(updateEmail: boolean) => setEmailOnSuccess(updateEmail)}
        onReportNameChange={(newName: string) => setReportName(newName)}
        isLoading={false}
        isValid={reportName !== "" && (specialtyFilter.includeFacilitySpecialties || specialtyFilter.includePhysicianSpecialties)}
        showSharedUsers={props.match.params.module === "adequacy"}
        sharedUsers={sharedUsers}
        updateSelectedSharedUsers={(newSharedUsers: number[]) => setSharedUsers(newSharedUsers)}
        exportType="ZipAnalysisAdequacyServicingProvider"
        projectId={props.projectId}
        networkAssessmentAnalysisType={NetworkAssessmentAnalysisType.ServiceArea}
      />
    </>
  );
}
